import { PageHero, SectionHeading, Reveal } from "@/components/Section";
import { IMAGES } from "@/data/site";
import { Ship, FileCheck, ArrowRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

const ExportFaq = ({ q, a, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`export-faq-${id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left text-white">
        <span className="font-display text-[20px] md:text-[22px] pr-6">{q}</span>
        <span className={`text-[#C8973C] text-3xl transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 pb-6" : "max-h-0"}`}>
        <p className="text-[#8A96A3] text-[16px] leading-7 pr-8">{a}</p>
      </div>
    </div>
  );
};

const EXPORT_CATEGORIES = [
  ["Hardware & Tools","Full hardware range for retail and industrial supply"],
  ["Building Materials","Cement, steel, roofing, tiles and construction materials"],
  ["Mining Equipment","Industrial mining supplies for mining operations across regions"],
  ["Heavy Equipment","Earthmoving, generating and industrial equipment"],
  ["Commodities","Steel, copper, construction chemicals and bulk raw materials"],
  ["Specialist Sourcing","We source and export on request — tell us what you need"],
];
const EXPORT_STEPS = [
  { n:"01 ENQUIRY", t:"Tell us what you need, quantities and destination country" },
  { n:"02 QUOTE", t:"Full export quote including product pricing, freight and documentation" },
  { n:"03 ORDER CONFIRMATION", t:"Confirm your order, pay deposit as agreed" },
  { n:"04 SOURCING & QC", t:"We source and quality-check all products before packing", img: IMAGES.exportQC, alt: "Workers performing quality control in a warehouse" },
  { n:"05 DOCUMENTATION", t:"Invoice, packing list, bill of lading, certificates of origin", img: IMAGES.exportPallet, alt: "Palletised goods wrapped and labelled ready for shipping" },
  { n:"06 FREIGHT & DELIVERY", t:"Door-to-door logistics or hand-off to your freight agent", img: IMAGES.exportTruck, alt: "Goods being loaded onto a truck for transport" },
];

const ExportProofSection = () => (
  <section className="bg-[#F8F6F2] py-20" data-testid="export-proof">
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
      <div className="bg-white border-l-[4px] border-[#C8973C] p-8 md:p-10 shadow-sm">
        <div className="text-[12px] tracking-[.22em] font-bold text-[#C8973C] mb-3">EXPORT PROOF</div>
        <h3 className="font-display text-[28px] md:text-[40px] leading-[1.05]">ESTABLISHED EXPORT CLIENTS IN MALAWI, TANZANIA AND ACROSS AFRICA.</h3>
        <p className="mt-5 text-[#1E2D40] leading-7 max-w-3xl">A consistent, documented, reliable supply chain across borders — hardware, building materials, mining equipment and industrial commodities delivered to active clients across the continent.</p>
      </div>
    </div>
  </section>
);

const WhatWeExportSection = () => (
  <section className="bg-[#0A1628] text-white py-24 blueprint-grid">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading gold light label="WHAT WE EXPORT" title="SIX CATEGORIES. ONE EXPORT DESK."/>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {EXPORT_CATEGORIES.map(([t,d],i)=> (
          <Reveal key={t} delay={i*70}>
            <div className="cg-card-dark cg-card-gold h-full" style={{background:"#1E2D40"}}>
              <h4 className="font-display text-[22px]">{t}</h4>
              <p className="mt-3 text-[#cbd3df] leading-7 text-[15px]">{d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CoverageSection = () => (
  <section className="bg-[#F8F6F2] py-24" data-testid="coverage-section">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading gold label="WHERE WE TRADE" title="ACTIVE ACROSS AFRICA. OPEN TO THE WORLD."
        subtitle="Established export clients in Malawi, Tanzania and across Africa. Open to enquiries from anywhere in the world." />
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 border-[3px] border-[#C8973C]" data-testid="trade-map">
          <iframe
            title="Cassie Gee — international trading coverage map"
            src="https://maps.google.com/maps?ll=0,20&z=3&t=m&output=embed&iwloc=near"
            width="100%" height="520" style={{ border: 0, display: "block" }}
            loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
        <div className="space-y-5">
          <div className="cg-card cg-card-gold" data-testid="africa-card">
            <div className="text-[12px] tracking-[.22em] font-bold text-[#C8973C]">AFRICA</div>
            <h4 className="font-display text-[22px] mt-2">OUR MOST ACTIVE TRADING REGION</h4>
            <p className="mt-3 text-[#1E2D40] leading-7 text-[15px]">Our most active and established trading region — with confirmed export clients in Malawi, Tanzania and across Africa.</p>
          </div>
          <div className="cg-card">
            <div className="text-[12px] tracking-[.22em] font-bold text-[#E8721C]">EUROPE · MIDDLE EAST · ASIA</div>
            <h4 className="font-display text-[22px] mt-2">OPEN TO GLOBAL ENQUIRIES</h4>
            <p className="mt-3 text-[#1E2D40] leading-7 text-[15px]">Sourcing and shipping enquiries from buyers anywhere in the world are welcome. Talk to us — we'll quote on freight, documentation and lead time.</p>
          </div>
          <div className="cg-card">
            <div className="text-[12px] tracking-[.22em] font-bold text-[#E8721C]">AMERICAS · OCEANIA</div>
            <h4 className="font-display text-[22px] mt-2">NEW ROUTES CONSIDERED</h4>
            <p className="mt-3 text-[#1E2D40] leading-7 text-[15px]">If you're sourcing hardware, mining or industrial supply for a project — tell us your destination and product list. We've shipped to harder places.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ExportProcessSection = () => (
  <section className="bg-[#0A1628] py-24 text-white blueprint-grid">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading gold light label="OUR EXPORT PROCESS" title="DOCUMENTED. ON-TIME. CROSS-BORDER." />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {EXPORT_STEPS.map((s) => (
          <div key={s.n} className="cg-card-dark cg-card-gold p-0 overflow-hidden" style={{background:"#1E2D40"}}>
            {s.img && (
              <div className="relative h-44 overflow-hidden">
                <img src={s.img} alt={s.alt} loading="lazy" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E2D40] via-[#1E2D40]/30 to-transparent"/>
              </div>
            )}
            <div className="p-7">
              <FileCheck className="text-[#C8973C]" size={26}/>
              <h4 className="font-display text-[20px] mt-3">{s.n}</h4>
              <p className="mt-2 text-[#cbd3df] leading-7 text-[15px]">{s.t}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ExportFaqSection = () => (
  <section className="bg-[#1E2D40] py-24 text-white">
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
      <SectionHeading gold light title="EXPORT FAQ" />
      <ExportFaq id="docs" q="Do you handle export documentation?" a="Yes. We prepare commercial invoices, packing lists, certificates of origin and coordinate with clearing agents."/>
      <ExportFaq id="min" q="What is the minimum export order?" a="We work with orders from a single pallet to full containers. Enquire and we'll advise on minimum viable order sizes by product."/>
      <ExportFaq id="freight" q="Can you coordinate freight to my country?" a="Yes. We coordinate freight to your destination or deliver to your nominated freight agent at our base."/>
      <ExportFaq id="time" q="How long does export delivery take?" a="Road freight to neighbouring countries typically ranges from 5–21 days depending on destination. Sea and air timelines vary by destination — we confirm in your quote."/>
      <ExportFaq id="inspect" q="Can I inspect goods before export?" a="Yes. Clients are welcome to inspect goods at our Pretoria facility before dispatch. We also provide photo documentation."/>
      <div className="mt-10"><Link to="/contact?type=export" className="btn-gold" data-testid="export-cta"><Ship size={16}/> Submit an Export Enquiry <ArrowRight size={14}/></Link></div>
    </div>
  </section>
);

const Export = () => (
  <>
    <PageHero
      gold
      label="EXPORT & INTERNATIONAL TRADING"
      title="WE SOURCE IT. WE SUPPLY IT. WHEREVER YOU NEED IT."
      subtitle="We are an established trading and export company with 23 years of supply experience. Our base is Pretoria. Our reach has no limits. Active export clients across Africa. Open to enquiries from anywhere in the world."
      image={IMAGES.exportHero}
    />
    <ExportProofSection />
    <WhatWeExportSection />
    <CoverageSection />
    <ExportProcessSection />
    <ExportFaqSection />
  </>
);
export default Export;
